import { useAmenityLabels } from '@/src/hooks/hotels/useAmenityLabels'
import { HotelReviewViewModel } from '@/src/types/hotels/details'
import { formatHotelDate } from '@/src/utils/hotels'
import { useLocale, useTranslations } from 'next-intl'
import { useMemo } from 'react'
import HotelHeaderCard, { HotelHeaderAmenity, ReviewAmenityKey } from './hotelHeaderCard'

export interface HotelHeaderCardContainerProps {
    variant: 'compact' | 'wide'
    hotel: HotelReviewViewModel['hotel']
    stay: HotelReviewViewModel['stay']
    /** Opens the date picker. Omit to render the stay dates as read-only. */
    onEditDates?: () => void
    /** Renders the search control as a labelled button rather than a bare icon. */
    searchLabel?: string
    /** Runs the search. Only the property (SEO) page supplies one. */
    onSearch?: () => void
    /** Rooms-and-guests control. Only the property (SEO) page supplies one. */
    guestsField?: React.ReactNode
}

const AMENITY_ORDER: ReviewAmenityKey[] = ['wifi', 'swim', 'parking', 'gym', 'roomService', 'bar']

const HotelHeaderCardContainer = ({
    variant,
    hotel,
    stay,
    onEditDates,
    searchLabel,
    onSearch,
    guestsField
}: HotelHeaderCardContainerProps) => {
    const t = useTranslations('hotels')
    const tCommon = useTranslations('common')
    const locale = useLocale()
    const amenityLabels = useAmenityLabels()

    const amenities = useMemo<HotelHeaderAmenity[]>(
        () => AMENITY_ORDER.map(key => ({ key, available: hotel.amenities[key], label: amenityLabels[key] })),
        [hotel.amenities, amenityLabels]
    )

    const checkInText = `${hotel.checkInTime ? `${hotel.checkInTime}, ` : ''}${formatHotelDate(stay.checkIn, locale)}`
    const checkOutText = `${hotel.checkOutTime ? `${hotel.checkOutTime}, ` : ''}${formatHotelDate(stay.checkOut, locale)}`

    return (
        <HotelHeaderCard
            variant={variant}
            name={hotel.name}
            address={hotel.address}
            starCount={hotel.starCount}
            amenities={amenities}
            checkInLabel={t('checkInLabel')}
            checkInText={checkInText}
            checkOutLabel={t('checkOutLabel')}
            checkOutText={checkOutText}
            editDatesLabel={t('changeDatesCta')}
            onEditDates={onEditDates}
            searchLabel={searchLabel}
            onSearch={onSearch}
            guestsField={guestsField}
            guestsLabel={`${tCommon('rooms')} & ${tCommon('guest', { s: 's' })}`}
            checkInDate={formatHotelDate(stay.checkIn, locale)}
            checkOutDate={formatHotelDate(stay.checkOut, locale)}
            nightsText={stay.nights > 0 ? t('nightsCount', { count: stay.nights }) : undefined}
        />
    )
}

export { HotelHeaderCardContainer }
export default HotelHeaderCardContainer
